/**
 * Google Places Address Autocomplete Controller (Updated for new AutocompleteSuggestion API)
 * Provides address autocomplete functionality and populates individual address fields
 */
import { Controller } from "@hotwired/stimulus";
import { Loader } from "@googlemaps/js-api-loader";

export default class extends Controller {
  static values = {
    apiKey: String,
    i18n: Object,
  };

  static targets = [
    "input",
    "address",
    "city",
    "postalCode",
    "manualFields",
    "suggestions",
  ];

  connect() {
    this.loader = new Loader({
      apiKey: this.apiKeyValue,
      version: "weekly",
      libraries: ["places"],
    });

    this.suggestions = [];
    this.selectedIndex = -1;
    this.sessionToken = null;
    this.debounceTimer = null;

    this.initializeAutocomplete();
  }

  async initializeAutocomplete() {
    try {
      const { AutocompleteSuggestion, AutocompleteSessionToken } =
        await this.loader.importLibrary("places");
      const { LatLng, LatLngBounds } = await this.loader.importLibrary("core");

      this.AutocompleteSuggestion = AutocompleteSuggestion;
      this.AutocompleteSessionToken = AutocompleteSessionToken;

      // Limit locations to lower BC.
      this.locationRestriction = new LatLngBounds(
        new LatLng(48.066334, -130.012207),
        new LatLng(52.498835, -115.158691),
      );
      this.boundHandleInput = this.handleInput.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleBlur = this.handleBlur.bind(this);
      this.inputTarget.addEventListener("input", this.boundHandleInput);
      this.inputTarget.addEventListener("keydown", this.boundHandleKeydown);
      this.inputTarget.addEventListener("blur", this.boundHandleBlur);
      this.createNewSession();
    } catch (error) {
      console.error("Error loading Google Places:", error);
      this.showError(this.i18nValue.errors.load_failed);
    }
  }

  createNewSession() {
    this.sessionToken = new this.AutocompleteSessionToken();
  }

  handleInput(event) {
    const query = event.target.value.trim();

    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (query.length < 2) {
      this.hideSuggestions();
      return;
    }

    // Debounce API calls
    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(query);
    }, 300);
  }

  async fetchSuggestions(query) {
    try {
      const request = {
        input: query,
        sessionToken: this.sessionToken,
        locationRestriction: this.locationRestriction,
        language: "en",
        region: "ca",
        includedRegionCodes: ["ca"],
        includedPrimaryTypes: ["street_address"],
      };

      const response =
        await this.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      this.suggestions = response.suggestions || [];
      this.displaySuggestions();
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      this.showError(this.i18nValue.errors.load_failed);
    }
  }

  displaySuggestions() {
    if (this.suggestions.length === 0) {
      this.hideSuggestions();
      return;
    }

    this.suggestionsTarget.innerHTML = "";
    this.selectedIndex = -1;

    this.suggestions.forEach((suggestion, index) => {
      if (suggestion.placePrediction) {
        const item = document.createElement("div");
        item.className = "autocomplete-item";
        item.innerHTML = suggestion.placePrediction.text.text;

        item.addEventListener("click", () => this.selectSuggestion(index));
        item.addEventListener("mouseenter", () => this.highlightItem(index));

        this.suggestionsTarget.appendChild(item);
      }
    });

    this.suggestionsTarget.style.display = "block";
  }

  highlightMatches(text, matches) {
    if (!matches || matches.length === 0) {
      return text;
    }

    let highlightedText = "";
    let lastIndex = 0;

    matches.forEach((match) => {
      highlightedText += text.substring(lastIndex, match.startOffset);
      highlightedText += `<strong>${text.substring(match.startOffset, match.endOffset)}</strong>`;
      lastIndex = match.endOffset;
    });

    highlightedText += text.substring(lastIndex);
    return highlightedText;
  }

  handleKeydown(event) {
    if (
      !this.hasSuggestionsTarget ||
      this.suggestionsTarget.style.display === "none"
    ) {
      return;
    }

    const items = this.suggestionsTarget.querySelectorAll(".autocomplete-item");

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.highlightItem(this.selectedIndex);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.highlightItem(this.selectedIndex);
        break;
      case "Enter":
        event.preventDefault();
        if (this.selectedIndex >= 0) {
          this.selectSuggestion(this.selectedIndex);
        }
        break;
      case "Escape":
        this.hideSuggestions();
        break;
    }
  }

  highlightItem(index) {
    const items = this.suggestionsTarget.querySelectorAll(".autocomplete-item");
    items.forEach((item, i) => {
      if (i === index) {
        item.style.backgroundColor = "#f0f0f0";
      } else {
        item.style.backgroundColor = "white";
      }
    });
    this.selectedIndex = index;
  }

  async selectSuggestion(index) {
    if (!this.suggestions[index] || !this.suggestions[index].placePrediction) {
      return;
    }

    const prediction = this.suggestions[index].placePrediction;
    this.hideSuggestions();

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress", "location"],
      });
      this.inputTarget.value = place.formattedAddress;
      this.populateAddressFields(place);
      this.hideError();
      this.createNewSession();
    } catch (error) {
      console.error("Error fetching place details:", error);
      this.showError(this.invalidSelectionErrorValue);
    }
  }

  populateAddressFields(place) {
    this.clearAddressFields();

    if (!place.addressComponents) {
      console.warn("No address components found");
      this.showError(this.invalidSelectionErrorValue);
      return;
    }

    let streetNumber = "";
    let route = "";
    let subpremise = "";
    place.addressComponents.forEach((component) => {
      const componentType = component.types[0];

      switch (componentType) {
        case "subpremise":
          subpremise = component.longText;
          break;
        case "street_number":
          streetNumber = component.longText;
          break;
        case "route":
          route = component.shortText;
          break;
        case "locality":
          if (this.hasCityTarget) {
            this.cityTarget.value = component.longText;
            this.cityTarget.dispatchEvent(
              new Event("input", { bubbles: true }),
            );
          }
          break;
        case "postal_code_prefix":
          if (
            this.hasPostalCodeTarget &&
            this.postalCodeTarget.value.length == 0
          ) {
            this.postalCodeTarget.value = component.longText;
            this.postalCodeTarget.dispatchEvent(
              new Event("input", { bubbles: true }),
            );
          }
        case "postal_code":
          if (this.hasPostalCodeTarget) {
            this.postalCodeTarget.value = component.longText;
            this.postalCodeTarget.dispatchEvent(
              new Event("input", { bubbles: true }),
            );
          }
          break;
      }
    });

    if (this.hasAddressTarget) {
      let fullAddress = [streetNumber, route].filter(Boolean).join(" ");
      if (subpremise) {
        fullAddress = fullAddress + ` #${subpremise}`;
      }
      if (fullAddress) {
        this.addressTarget.value = fullAddress;
        this.addressTarget.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  }

  handleBlur(event) {
    setTimeout(() => {
      if (!this.suggestionsTarget.matches(":hover")) {
        this.hideSuggestions();
      }
    }, 150);
  }

  hideSuggestions() {
    if (this.suggestionsTarget) {
      this.suggestionsTarget.style.display = "none";
    }
    this.suggestions = [];
    this.selectedIndex = -1;
  }

  clearAddressFields() {
    if (this.hasAddressTarget) {
      this.addressTarget.value = "";
    }
    if (this.hasCityTarget) {
      this.cityTarget.value = "";
    }
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.value = "";
    }
  }

  showError(message) {
    this.hideError();

    const errorDiv = document.createElement("div");
    errorDiv.className = "form-text text-danger mt-1";
    errorDiv.id = "address-autocomplete-error";
    errorDiv.textContent = message;

    this.inputTarget.parentNode.appendChild(errorDiv);
  }

  hideError() {
    const existingError = document.getElementById("address-autocomplete-error");
    if (existingError) {
      existingError.remove();
    }
  }

  toggleManualEntry() {
    const manualFields = this.manualFieldsTarget;
    const autocompleteField = this.inputTarget.closest(".col");
    const toggleLink = document.querySelector(".manual-address-toggle");

    if (manualFields && autocompleteField && toggleLink) {
      manualFields.classList.toggle("d-none");
      autocompleteField.classList.toggle("d-none");
      this.clearAutocompleteInput();
      this.clearAddressFields();
    }
  }

  clearAutocompleteInput() {
    this.inputTarget.value = "";
    this.hideSuggestions();
    this.hideError();
    this.createNewSession();
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (this.boundHandleInput) {
      this.inputTarget.removeEventListener("input", this.boundHandleInput);
      this.inputTarget.removeEventListener("keydown", this.boundHandleKeydown);
      this.inputTarget.removeEventListener("blur", this.boundHandleBlur);
    }

    if (this.suggestionsTarget && this.suggestionsTarget.parentNode) {
      this.suggestionsTarget.parentNode.removeChild(this.suggestionsTarget);
    }

    this.hideError();
  }
}
